import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  getLeadsPromise,
  deleteLeadPromise,
  deleteLeadsPromise,
  getLeadListByIdPromise,
  cancelLeadListImport,
} from "../services/apiService";
import { useModal } from "../components/modal/ModalContext";
import { toast } from "react-toastify";
import usePageTitle from "../helpers/usePageTitle";
import Pagination from "../components/shared/Pagination";
import DataGrid from "../components/shared/DataGrid";
import SearchInput from "../components/shared/SearchInput";
import Checkbox from "../components/shared/Checkbox";
import EmptyState from "../components/shared/EmptyState";
import LoadingOverlay from "../components/shared/LoadingOverlay";
import SelectBox from "../components/shared/SelectBox";
import LeadModal from "../components/leads/LeadModal";
import LeadListModal from "../components/leadLists/LeadListModal";
import useImportProgress from "../hooks/useImportProgress";
import { getErrorMessage, notifyEventError } from "../helpers/apiError";

// Status badge configs
const CONNECTION_STATUS_COLORS = {
  NOT_CONNECTED: "bg-gray-100 text-gray-600",
  CONNECTION_SENT: "bg-blue-100 text-blue-700",
  CONNECTED: "bg-green-100 text-green-700",
  CONNECTION_REJECTED: "bg-red-100 text-red-700",
  CONNECTION_WITHDRAWN: "bg-orange-100 text-orange-700",
};

const MESSAGE_STATUS_COLORS = {
  NOT_SENT: "bg-gray-100 text-gray-500",
  SENT: "bg-blue-100 text-blue-700",
  DELIVERED: "bg-teal-100 text-teal-700",
  READ: "bg-green-100 text-green-700",
  REPLIED: "bg-purple-100 text-purple-700",
  FAILED: "bg-red-100 text-red-700",
};

const StatusBadge = ({ value, colorMap, translationPrefix }) => {
  const { t } = useTranslation();
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  const colorClass = colorMap[value] || "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
      {t(`${translationPrefix}.${value}`, { defaultValue: value })}
    </span>
  );
};

const LeadListDetail = () => {
  const { t } = useTranslation();
  const { lng, listId } = useParams();
  const navigate = useNavigate();
  usePageTitle("leads");

  const [leadList, setLeadList] = useState(null);
  const [isListLoading, setIsListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: "" });
  const [searchValue, setSearchValue] = useState("");
  const [connectionStatusFilter, setConnectionStatusFilter] = useState("");
  const [messageStatusFilter, setMessageStatusFilter] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedLeads, setSelectedLeads] = useState([]);

  const { showModal } = useModal();

  const connectionStatusOptions = [
    { value: "NOT_CONNECTED", label: t("connectionStatus.NOT_CONNECTED") },
    { value: "CONNECTION_SENT", label: t("connectionStatus.CONNECTION_SENT") },
    { value: "CONNECTED", label: t("connectionStatus.CONNECTED") },
    { value: "CONNECTION_REJECTED", label: t("connectionStatus.CONNECTION_REJECTED") },
    { value: "CONNECTION_WITHDRAWN", label: t("connectionStatus.CONNECTION_WITHDRAWN") },
  ];

  const messageStatusOptions = [
    { value: "NOT_SENT", label: t("messageStatus.NOT_SENT") },
    { value: "SENT", label: t("messageStatus.SENT") },
    { value: "DELIVERED", label: t("messageStatus.DELIVERED") },
    { value: "READ", label: t("messageStatus.READ") },
    { value: "REPLIED", label: t("messageStatus.REPLIED") },
    { value: "FAILED", label: t("messageStatus.FAILED") },
  ];

  const fetchLeadList = async ({ silent = false } = {}) => {
    if (!silent) setIsListLoading(true);
    try {
      const response = await getLeadListByIdPromise(listId);
      setLeadList(response.data);
    } catch (err) {
      setListError(getErrorMessage(err, t("leadLists.toast.error")));
    } finally {
      if (!silent) setIsListLoading(false);
    }
  };

  const fetchLeads = async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    try {
      const response = await getLeadsPromise(listId, {
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      const { data, page, limit, total, totalPages, hasNext, hasPrev } = response.data;
      setLeads(data || []);
      setPagination({ page, limit, total, totalPages, hasNext, hasPrev });
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, filters, pagination.page]);

  // Progress events arrive per lead; refetch the table at most every 3s to avoid 429s.
  const lastLeadsRefetchRef = React.useRef(0);

  // Live import progress via Socket.IO (replaces 1s dual HTTP poll)
  useImportProgress((event) => {
    const eventListId = event.leadListId || event.leadId;
    if (eventListId !== listId) return;
    setLeadList((prev) =>
      prev
        ? {
            ...prev,
            importStatus: event.status || prev.importStatus,
            importScraped: event.scraped ?? prev.importScraped,
            importTarget: event.target ?? prev.importTarget,
          }
        : prev
    );
    const isFinal = ["COMPLETED", "FAILED", "CANCELLED"].includes(event.status);
    if (isFinal || (event.scraped != null && Date.now() - lastLeadsRefetchRef.current >= 3000)) {
      lastLeadsRefetchRef.current = Date.now();
      fetchLeads({ silent: true });
    }
    if (isFinal) {
      fetchLeadList({ silent: true });
    }
    if (event.status === "FAILED") {
      notifyEventError(event, t("leads.toast.error"));
    }
  });

  const handleStopImport = async () => {
    try {
      await cancelLeadListImport(listId);
      toast.success(t("leads.import.stopImporting"));
      fetchLeadList();
      fetchLeads();
    } catch (err) {
      toast.error(getErrorMessage(err, t("leads.toast.error")));
    }
  };

  const importPercent =
    leadList?.importTarget > 0
      ? Math.min(100, Math.round(((leadList.importScraped || 0) / leadList.importTarget) * 100))
      : 0;

  const handleFilter = () => {
    const newFilters = { search: searchValue };
    if (connectionStatusFilter) newFilters.connectionStatus = connectionStatusFilter;
    if (messageStatusFilter) newFilters.messageStatus = messageStatusFilter;
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((lead) => lead.id));
    }
  };

  const refreshAfterImport = async (importResult) => {
    // Optimistic: mount banner immediately; WS pushes scraped/target thereafter
    if (importResult?.importStatus === "IMPORTING" || importResult?.jobId) {
      setLeadList((prev) =>
        prev
          ? {
              ...prev,
              importStatus: "IMPORTING",
              importJobId: importResult.jobId != null ? String(importResult.jobId) : prev.importJobId,
              importTarget: importResult.limit ?? prev.importTarget ?? 1000,
              importScraped: 0,
            }
          : prev
      );
    }
    await Promise.all([
      fetchLeadList({ silent: true }),
      fetchLeads({ silent: true }),
    ]);
  };

  const openAddLeadModal = () => {
    showModal("leads.add", <LeadModal listId={listId} onSuccess={refreshAfterImport} />);
  };

  const openEditLeadModal = (lead) => {
    showModal("leads.edit", <LeadModal listId={listId} lead={lead} onSuccess={fetchLeads} />);
  };

  const openEditListModal = () => {
    showModal("leadLists.editTitle", (
      <LeadListModal
        leadList={leadList}
        onSuccess={() => {
          fetchLeadList();
        }}
      />
    ));
  };

  const handleDeleteLead = async (leadId) => {
    const original = [...leads];
    setLeads(leads.filter((l) => l.id !== leadId));
    try {
      await deleteLeadPromise(listId, leadId);
      toast.success(t("leads.toast.deleteSuccess") || "Lead deleted");
    } catch {
      setLeads(original);
      toast.error(t("leads.toast.error"));
    }
  };

  const handleDeleteSelectedLeads = async () => {
    const original = [...leads];
    const toDelete = [...selectedLeads];
    setLeads(leads.filter((l) => !toDelete.includes(l.id)));
    setSelectedLeads([]);
    try {
      await deleteLeadsPromise(listId, toDelete);
      toast.success(`${toDelete.length} ${t("leads.deleteSelected")}` );
    } catch {
      setLeads(original);
      toast.error(t("leads.toast.error"));
    }
  };

  const columns = useMemo(
    () => [
      {
        header: (
          <Checkbox
            checked={selectedLeads.length === leads.length && leads.length > 0}
            onChange={handleSelectAll}
          />
        ),
        span: 1,
        center: true,
      },
      { header: t("leads.name"), span: 2 },
      { header: t("leads.jobTitle"), span: 2 },
      { header: t("leads.companyAndEmail"), span: 2 },
      { header: t("leads.connectionStatus"), span: 2, center: true },
      { header: t("leads.messageStatus"), span: 2, center: true },
      { header: t("leads.actions"), span: 1, center: true },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLeads.length, leads.length, t]
  );

  const renderRow = (lead) => (
    <div
      key={lead.id}
      className="grid items-center grid-cols-12 gap-2 px-4 py-3 bg-white rounded-md shadow"
    >
      {/* Checkbox */}
      <div className="col-span-1 text-center">
        <Checkbox
          checked={selectedLeads.includes(lead.id)}
          onChange={() => handleSelectLead(lead.id)}
        />
      </div>

      {/* Name + LinkedIn */}
      <div className="col-span-2">
        <p className="font-semibold truncate">{`${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "—"}</p>
        {lead.location && <p className="text-xs text-gray-500 truncate">{lead.location}</p>}
      </div>

      {/* Job Title */}
      <div className="col-span-2 text-sm truncate">{lead.jobTitle || "—"}</div>

      {/* Company + Email */}
      <div className="col-span-2">
        <p className="text-sm truncate">{lead.company || "—"}</p>
        <p className="text-xs text-gray-500 truncate">{lead.email || ""}</p>
      </div>

      {/* Connection Status */}
      <div className="col-span-2 flex justify-center">
        <StatusBadge
          value={lead.connectionStatus}
          colorMap={CONNECTION_STATUS_COLORS}
          translationPrefix="connectionStatus"
        />
      </div>

      {/* Message Status */}
      <div className="col-span-2 flex justify-center">
        <StatusBadge
          value={lead.messageStatus}
          colorMap={MESSAGE_STATUS_COLORS}
          translationPrefix="messageStatus"
        />
      </div>

      {/* Actions */}
      <div className="col-span-1 flex justify-center">
        <LeadActionsDropdown
          onEdit={() => openEditLeadModal(lead)}
          onDelete={() => handleDeleteLead(lead.id)}
        />
      </div>
    </div>
  );

  // Keep chrome + progress banner visible; only block on initial list load
  if (isListLoading && !leadList) return <LoadingOverlay />;
  if (isLoading && leads.length === 0 && leadList?.importStatus !== "IMPORTING") {
    return <LoadingOverlay />;
  }

  return (
    <div className="py-[20px]">
      {/* Back link */}
      <button
        onClick={() => navigate(`/${lng}/leads`)}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-taction mb-4"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
        {t("leadLists.title")}
      </button>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{leadList?.name}</h1>
          <button
            onClick={openEditListModal}
            className="p-1 text-gray-400 hover:text-taction"
            title={t("leadLists.edit")}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>
        <button
          onClick={openAddLeadModal}
          className="text-[15px] bg-blue-600 text-white font-bold px-[15px] rounded-[8px] inline-block leading-[35px] select-none cursor-pointer hover:opacity-90 transition-opacity"
        >
          {t("leads.add")}
        </button>
      </div>

      {listError && <div className="text-red-500 mb-4">{listError}</div>}

      {leadList?.importStatus === "IMPORTING" && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div>
              <p className="text-sm font-semibold text-blue-900">{t("leads.import.banner")}</p>
              <p className="text-xs text-blue-700 mt-0.5">
                {t("leads.import.progress", {
                  scraped: leadList.importScraped || 0,
                  total: leadList.importTarget || 1000,
                  percent: importPercent,
                })}
              </p>
              {leadList.searchUrl && (
                <p className="text-[11px] text-blue-600/80 mt-1 truncate max-w-xl" title={leadList.searchUrl}>
                  {t("leads.import.searchUrlSaved")}: {leadList.searchUrl}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleStopImport}
              className="px-3 py-1.5 text-sm font-semibold text-white bg-red-500 rounded-md hover:bg-red-600"
            >
              {t("leads.import.stopImporting")}
            </button>
          </div>
          <div className="h-2 w-full rounded-full bg-blue-100 overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${importPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[200px]">
          <SearchInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="leads.searchPlaceholder"
          />
        </div>
        <div className="w-44">
          <SelectBox
            name="connectionStatus"
            value={connectionStatusFilter}
            onChange={(e) => setConnectionStatusFilter(e.target.value)}
            options={connectionStatusOptions}
            placeholder={t("leads.connectionStatus")}
            mb={false}
          />
        </div>
        <div className="w-40">
          <SelectBox
            name="messageStatus"
            value={messageStatusFilter}
            onChange={(e) => setMessageStatusFilter(e.target.value)}
            options={messageStatusOptions}
            placeholder={t("leads.messageStatus")}
            mb={false}
          />
        </div>
        <button
          onClick={handleFilter}
          className="px-4 py-2 text-white rounded-md bg-blue-600 hover:opacity-90 font-bold"
        >
          {t("leads.filter")}
        </button>
        {selectedLeads.length > 0 && (
          <>
            <span className="text-sm text-gray-600">
              {t("leads.selected", { count: selectedLeads.length })}
            </span>
            <button
              onClick={handleDeleteSelectedLeads}
              className="px-4 py-2 text-white bg-red-500 rounded-md hover:bg-red-600 font-bold"
            >
              {t("leads.deleteSelected")}
            </button>
          </>
        )}
      </div>

      {error && <div className="text-red-500 mb-4">{error}</div>}

      {leads.length === 0 ? (
        leadList?.importStatus === "IMPORTING" ? (
          <div className="rounded-lg border border-dashed border-blue-200 bg-white px-6 py-10 text-center text-sm text-blue-800">
            {t("leads.import.waitingForLeads", {
              defaultValue: "Import in progress — leads will show up here as they are scraped.",
            })}
          </div>
        ) : (
          <EmptyState
            message="leads.noLeads"
            cta="leads.add"
            onCtaClick={openAddLeadModal}
          />
        )
      ) : (
        <>
          <DataGrid columns={columns} data={leads} renderRow={renderRow} />
          <div className="mt-4">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              hasNext={pagination.hasNext}
              hasPrev={pagination.hasPrev}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

// Inline actions dropdown
const LeadActionsDropdown = ({ onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const action = (fn) => () => { fn(); setIsOpen(false); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-500 rounded-full hover:bg-gray-100"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 z-10 w-40 mt-2 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="py-1">
            <button onClick={action(onEdit)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              {t("leads.edit")}
            </button>
            <hr className="my-1" />
            <button onClick={action(onDelete)} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
              {t("leads.delete")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadListDetail;
